import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarChart3, Users, Stethoscope, ClipboardList, Activity } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_ICONS = {
  doctor: <Stethoscope className="w-5 h-5 text-blue-500" />,
  nurse: <Activity className="w-5 h-5 text-emerald-500" />,
  reception: <ClipboardList className="w-5 h-5 text-violet-500" />
};

const Productivity = () => {
  const [stats, setStats] = useState([]);
  const [period, setPeriod] = useState('today');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, [period]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/productivity?period=${period}`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch productivity stats:', error);
      toast.error('Failed to load productivity data');
    } finally {
      setLoading(false);
    }
  };

  const getPeriodLabel = () => {
    switch (period) {
      case 'today': return "Today's";
      case 'week': return 'This Week';
      case 'month': return 'This Month';
      default: return '';
    }
  };

  const getTotalByRole = (role, field) => {
    return stats.filter(s => s.role === role).reduce((sum, s) => sum + (s[field] || 0), 0);
  };

  if (loading) {
    return (
      <Layout>
        <div className="page-container flex items-center justify-center min-h-[50vh]">
          <div className="text-slate-600">Loading...</div>
        </div>
      </Layout>
    );
  }

  const doctors = stats.filter(s => s.role === 'doctor');
  const nurses = stats.filter(s => s.role === 'nurse');
  const receptions = stats.filter(s => s.role === 'reception');

  return (
    <Layout>
      <div className="page-container" data-testid="productivity-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">Staff Productivity</h1>
            <p className="text-slate-600 mt-1">{getPeriodLabel()} activity overview</p>
          </div>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card p-6 border-2 border-blue-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600">Encounters</p>
                <p className="text-3xl font-bold text-blue-600">{getTotalByRole('doctor', 'encounters_count')}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Stethoscope className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">By doctors</p>
          </div>
          
          <div className="card p-6 border-2 border-emerald-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600">Vitals Recorded</p>
                <p className="text-3xl font-bold text-emerald-600">{getTotalByRole('nurse', 'vitals_count')}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Activity className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">With vitals data</p>
          </div>
          
          <div className="card p-6 border-2 border-violet-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600">Registrations</p>
                <p className="text-3xl font-bold text-violet-600">{getTotalByRole('reception', 'registrations_count')}</p>
              </div>
              <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-violet-600" />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">New patients</p>
          </div>
        </div>

        {/* Detailed Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Doctors Table */}
          <div className="card" data-testid="doctors-productivity">
            <div className="p-4 border-b-2 border-slate-200 flex items-center gap-2">
              <Stethoscope className="w-5 h-5 text-blue-500" />
              <h2 className="font-semibold text-slate-900">Doctors</h2>
            </div>
            {doctors.length > 0 ? (
              <div className="divide-y-2 divide-slate-100">
                {doctors.map(doc => (
                  <div key={doc.user_id} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center font-semibold text-blue-700">
                        {doc.user_name.charAt(0)}
                      </div>
                      <span className="font-medium text-slate-900">{doc.user_name}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-blue-600">{doc.encounters_count}</p>
                      <p className="text-xs text-slate-500">encounters</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">No doctors found</div>
            )}
          </div>

          {/* Nurses Table */}
          <div className="card" data-testid="nurses-productivity">
            <div className="p-4 border-b-2 border-slate-200 flex items-center gap-2">
              <Activity className="w-5 h-5 text-emerald-500" />
              <h2 className="font-semibold text-slate-900">Nurses</h2>
            </div>
            {nurses.length > 0 ? (
              <div className="divide-y-2 divide-slate-100">
                {nurses.map(nurse => (
                  <div key={nurse.user_id} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center font-semibold text-emerald-700">
                        {nurse.user_name.charAt(0)}
                      </div>
                      <span className="font-medium text-slate-900">{nurse.user_name}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-emerald-600">{nurse.vitals_count}</p>
                      <p className="text-xs text-slate-500">vitals</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">No nurses found</div>
            )}
          </div>

          {/* Reception Table */}
          <div className="card" data-testid="reception-productivity">
            <div className="p-4 border-b-2 border-slate-200 flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-violet-500" />
              <h2 className="font-semibold text-slate-900">Reception</h2>
            </div>
            {receptions.length > 0 ? (
              <div className="divide-y-2 divide-slate-100">
                {receptions.map(rec => (
                  <div key={rec.user_id} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center font-semibold text-violet-700">
                        {rec.user_name.charAt(0)}
                      </div>
                      <span className="font-medium text-slate-900">{rec.user_name}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-violet-600">{rec.registrations_count}</p>
                      <p className="text-xs text-slate-500">registrations</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">No reception staff found</div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Productivity;
